/**
 * Der Regelkern.
 *
 * Eine reine Zustandsmaschine: `anwenden(zustand, aktion)` gibt einen neuen Zustand
 * zurueck und veraendert nichts. Kein Rendering, keine Zeit, kein globaler Zufall.
 * Dadurch ist jede Regel ohne Oberflaeche pruefbar, jede Partie wiederholbar und ein
 * spaeterer Netzwerkbetrieb moeglich, ohne den Kern anzufassen.
 *
 * Die Regeln stehen in wiki/FuenfSterne/topics/regelwerk.md, die Zahlen in
 * topics/spieldaten.md.
 */
import { HOTELS, type Hotel, type HotelId } from './data/hotels'
import { FELDER, START_FELD, angrenzendeGelaende, eingangsFelder } from './data/spielplan'
import { bauwuerfel, wuerfel, zufallAus } from './rng'
import type {
  Aktion,
  Auktion,
  Besitz,
  Regeln,
  Schuld,
  Spieler,
  SpielerId,
  Zugrechte,
  Zustand,
} from './typen'
import { KEINE_RECHTE, LEERE_ANZEIGE, STANDARDREGELN } from './typen'

// ---------------------------------------------------------------------------
// Abfragen
// ---------------------------------------------------------------------------

export function spieler(z: Zustand, id: SpielerId): Spieler {
  const s = z.spieler[id]
  if (!s) throw new Error(`Unbekannter Spieler ${id}`)
  return s
}

export function aktive(z: Zustand): readonly Spieler[] {
  return z.spieler.filter((s) => !s.ausgeschieden)
}

export function besitzVon(z: Zustand, id: SpielerId): readonly Besitz[] {
  return Object.values(z.besitz).filter((b): b is Besitz => !!b && b.besitzer === id)
}

/** Die Ausbaustufe, die den Nachtsatz bestimmt: letztes Gebaeude bzw. Freizeitpark. */
export function stufeVon(b: Besitz): { sterne: number; nachtsatz: number } | null {
  const hotel = HOTELS[b.hotel]
  if (b.freizeitpark) return hotel.freizeitpark
  if (b.gebaeude === 0) return null
  return hotel.gebaeude[b.gebaeude - 1]!
}

export function nachtsatz(z: Zustand, hotel: HotelId): number {
  const b = z.besitz[hotel]
  return b ? (stufeVon(b)?.nachtsatz ?? 0) : 0
}

export function sterne(z: Zustand, hotel: HotelId): number {
  const b = z.besitz[hotel]
  return b ? (stufeVon(b)?.sterne ?? 0) : 0
}

export function istKomplett(b: Besitz): boolean {
  return b.gebaeude === HOTELS[b.hotel].gebaeude.length
}

/**
 * Darf der Freizeitpark angelegt werden? Erst, wenn das Hotel komplett ist **und**
 * seither eine Runde vergangen ist - die Anleitung sagt "in jeder beliebigen der
 * nachfolgenden Runden".
 */
export function parkErlaubt(z: Zustand, b: Besitz): boolean {
  return (
    istKomplett(b) &&
    !b.freizeitpark &&
    b.komplettSeitRunde !== null &&
    z.runde > b.komplettSeitRunde
  )
}

/** Setzt `komplettSeitRunde`, sobald das letzte Gebaeude steht. */
function mitFertigstellung(z: Zustand, b: Besitz): Besitz {
  if (!istKomplett(b) || b.komplettSeitRunde !== null) return b
  return { ...b, komplettSeitRunde: z.runde }
}

/** Buchwert eines Besitzes: Grundstueck plus alles, was darauf steht. */
export function buchwert(b: Besitz): number {
  const hotel = HOTELS[b.hotel]
  let w = hotel.grundstueck
  for (let i = 0; i < b.gebaeude; i++) w += hotel.gebaeude[i]!.kosten
  if (b.freizeitpark) w += hotel.freizeitpark.kosten
  return w
}

export function vermoegen(z: Zustand, id: SpielerId): number {
  return besitzVon(z, id).reduce((n, b) => n + buchwert(b), spieler(z, id).konto)
}

/** Felder, an denen dieser Spieler noch einen Eingang zu diesem Hotel setzen darf. */
export function freieEingangsFelder(z: Zustand, hotel: HotelId): readonly number[] {
  const belegt = new Set(z.eingaenge.map((e) => e.feld))
  return eingangsFelder(hotel).filter((f) => !belegt.has(f))
}

export function eingangAuf(z: Zustand, feld: number): { hotel: HotelId; besitzer: SpielerId } | null {
  const e = z.eingaenge.find((x) => x.feld === feld)
  if (!e) return null
  const b = z.besitz[e.hotel]
  return b ? { hotel: e.hotel, besitzer: b.besitzer } : null
}

// ---------------------------------------------------------------------------
// Aufbau
// ---------------------------------------------------------------------------

export interface SpielerVorgabe {
  readonly name: string
  readonly farbe: string
  readonly bot: boolean
}

export const SPIELERFARBEN = ['#d94f3d', '#3d7ad9', '#3da85c', '#e0b022'] as const

export function neuesSpiel(
  vorgaben: readonly SpielerVorgabe[],
  saat = 1,
  regeln: Regeln = STANDARDREGELN,
): Zustand {
  if (vorgaben.length < 2 || vorgaben.length > 4) {
    throw new Error('Fuenf Sterne wird zu zweit bis zu viert gespielt')
  }
  const liste: Spieler[] = vorgaben.map((v, i) => ({
    id: i,
    name: v.name,
    farbe: v.farbe,
    bot: v.bot,
    konto: regeln.startkapital,
    feld: START_FELD,
    ausgeschieden: false,
  }))
  return {
    regeln,
    spieler: liste,
    amZug: 0,
    runde: 1,
    bank: -regeln.startkapital * liste.length,
    besitz: {},
    eingaenge: [],
    rechte: KEINE_RECHTE,
    phase: 'wuerfeln',
    schuld: null,
    auktion: null,
    offeneRechnung: null,
    offenerBau: null,
    anzeige: LEERE_ANZEIGE,
    zufall: zufallAus(saat),
    protokoll: [{ runde: 1, spieler: null, text: 'Die Partie beginnt.' }],
    sieger: null,
  }
}

// ---------------------------------------------------------------------------
// Bausteine
// ---------------------------------------------------------------------------

/**
 * Geldbetrag fuer das Protokoll. Das Protokoll ist Teil des Zustands und wird
 * unveraendert angezeigt, deshalb steht es hier in lesbarem Deutsch.
 */
function betrag(n: number): string {
  return `${n.toLocaleString('de-DE')} €`
}

function notiere(z: Zustand, text: string, id: SpielerId | null = z.amZug): Zustand {
  return { ...z, protokoll: [...z.protokoll, { runde: z.runde, spieler: id, text }] }
}

function mitSpieler(z: Zustand, id: SpielerId, aenderung: Partial<Spieler>): Zustand {
  return {
    ...z,
    spieler: z.spieler.map((s) => (s.id === id ? { ...s, ...aenderung } : s)),
  }
}

type Konto = SpielerId | 'bank'

/**
 * Verschiebt Geld. Die Summe aller Spielerkonten plus Bankkonto bleibt konstant -
 * das ist die Erhaltungsgroesse, die der Komplettpartie-Test ueberwacht.
 */
function ueberweise(z: Zustand, von: Konto, an: Konto, wert: number): Zustand {
  let out = z
  if (von === 'bank') out = { ...out, bank: out.bank - wert }
  else out = mitSpieler(out, von, { konto: spieler(out, von).konto - wert })
  if (an === 'bank') out = { ...out, bank: out.bank + wert }
  else out = mitSpieler(out, an, { konto: spieler(out, an).konto + wert })
  return out
}

function geldsumme(z: Zustand): number {
  return z.spieler.reduce((n, s) => n + s.konto, 0) + z.bank
}

export { geldsumme }

function mitBesitz(z: Zustand, hotel: HotelId, b: Besitz | null): Zustand {
  const besitz = { ...z.besitz }
  if (b) besitz[hotel] = b
  else delete besitz[hotel]
  return { ...z, besitz }
}

function mitRechten(z: Zustand, aenderung: Partial<Zugrechte>): Zustand {
  return { ...z, rechte: { ...z.rechte, ...aenderung } }
}

function feldBesetzt(z: Zustand, feld: number, ausser: SpielerId): boolean {
  if (feld === START_FELD) return false // der Parkplatz fasst alle Autos
  return z.spieler.some((s) => !s.ausgeschieden && s.id !== ausser && s.feld === feld)
}

function mod(n: number): number {
  return ((n % FELDER.length) + FELDER.length) % FELDER.length
}

/** Felder, die auf dem Weg von `von` nach `bis` betreten werden, `bis` eingeschlossen. */
function betreteneFelder(von: number, bis: number): number[] {
  const out: number[] = []
  let k = von
  while (k !== bis) {
    k = mod(k + 1)
    out.push(k)
  }
  return out
}

// ---------------------------------------------------------------------------
// Zugablauf
// ---------------------------------------------------------------------------

function zielFeld(z: Zustand, start: number, augen: number): number {
  const roh = mod(start + augen)
  if (!feldBesetzt(z, roh, z.amZug)) return roh
  const richtung = z.regeln.blockierteFelder === 'zurueck' ? -1 : 1
  for (let i = 1; i < FELDER.length; i++) {
    const k = mod(roh + richtung * i)
    // Nie hinter den eigenen Startpunkt zurueck: sonst waere der Zug ein Rueckzug.
    if (richtung === -1 && k === start) break
    if (!feldBesetzt(z, k, z.amZug)) return k
  }
  for (let i = 1; i < FELDER.length; i++) {
    const k = mod(roh + i)
    if (!feldBesetzt(z, k, z.amZug)) return k
  }
  return roh
}

function bankZahltNoch(z: Zustand): boolean {
  // Sind von urspruenglich drei oder vier Spielern nur noch zwei uebrig,
  // gibt es beim Passieren der Bank kein Geld mehr.
  return !(z.spieler.length >= 3 && aktive(z).length === 2)
}

function bewege(z: Zustand, augen: number): Zustand {
  const start = spieler(z, z.amZug).feld
  const ziel = zielFeld(z, start, augen)
  const weg = betreteneFelder(start, ziel)
  let out = mitSpieler(z, z.amZug, { feld: ziel })
  out = { ...out, anzeige: { ...out.anzeige, weg } }

  for (const k of weg) {
    const feld = FELDER[k]!
    if (feld.linien.includes('bank')) {
      if (bankZahltNoch(out)) {
        out = ueberweise(out, 'bank', out.amZug, out.regeln.bankpassage)
        out = notiere(out, `passiert die Bank und kassiert ${betrag(out.regeln.bankpassage)}.`)
      } else {
        out = notiere(out, 'passiert die Bank - im Endspiel zu zweit zahlt sie nicht mehr.')
      }
    }
    if (feld.linien.includes('rathaus')) {
      out = mitRechten(out, { rathaus: true })
      out = notiere(out, 'passiert das Rathaus und darf Eingänge kaufen.')
    }
  }
  return out
}

function feldRechte(z: Zustand): Zustand {
  const feld = FELDER[spieler(z, z.amZug).feld]!
  switch (feld.art) {
    case 'bau':
      return mitRechten(z, { darfGenehmigungBeantragen: true })
    case 'eingang-gratis':
      return mitRechten(z, { gratisEingaenge: z.rechte.gratisEingaenge + 1 })
    case 'bauphase-gratis':
      return mitRechten(z, { gratisBauphasen: z.rechte.gratisBauphasen + 1 })
    default:
      return z
  }
}

function nachAnkunft(z: Zustand): Zustand {
  const feld = spieler(z, z.amZug).feld
  const eingang = eingangAuf(z, feld)
  if (eingang && eingang.besitzer !== z.amZug) {
    return { ...feldRechte(z), phase: 'uebernachten' }
  }
  return { ...feldRechte(z), phase: 'handeln' }
}

function naechsterSpieler(z: Zustand): Zustand {
  const uebrig = aktive(z)
  if (uebrig.length <= 1) return beendeSpiel(z)

  if (z.rechte.nochmal) {
    return { ...z, rechte: { ...KEINE_RECHTE }, anzeige: LEERE_ANZEIGE, phase: 'wuerfeln' }
  }

  let naechster = z.amZug
  for (let i = 1; i <= z.spieler.length; i++) {
    const k = (z.amZug + i) % z.spieler.length
    if (!z.spieler[k]!.ausgeschieden) {
      naechster = k
      break
    }
  }
  const erster = uebrig[0]!.id
  const runde = naechster === erster ? z.runde + 1 : z.runde
  const out: Zustand = {
    ...z,
    amZug: naechster,
    runde,
    rechte: { ...KEINE_RECHTE },
    anzeige: LEERE_ANZEIGE,
    phase: 'wuerfeln',
  }
  if (out.regeln.rundenlimit !== null && runde > out.regeln.rundenlimit) {
    return beendeSpiel(notiere(out, 'Das Rundenlimit ist erreicht.', null))
  }
  return out
}

function beendeSpiel(z: Zustand): Zustand {
  const uebrig = aktive(z)
  if (uebrig.length === 0) return { ...z, phase: 'ende', sieger: null }
  const bester = uebrig.reduce((a, b) => (vermoegen(z, a.id) >= vermoegen(z, b.id) ? a : b))
  return {
    ...notiere(z, 'gewinnt die Partie.', bester.id),
    phase: 'ende',
    sieger: bester.id,
  }
}

// ---------------------------------------------------------------------------
// Zahlungen und Schulden
// ---------------------------------------------------------------------------

/**
 * Fordert einen Betrag ein. Reicht das Bargeld nicht, wechselt das Spiel in die
 * Phase "schuld": der Spieler muss versteigern, bis er zahlen kann.
 */
function fordere(z: Zustand, wert: number, an: Konto, grund: string): Zustand {
  const s = spieler(z, z.amZug)
  if (s.konto >= wert) {
    return notiere(ueberweise(z, z.amZug, an, wert), `zahlt ${betrag(wert)} (${grund}).`)
  }
  const schuld: Schuld = { betrag: wert, an, grund }
  const offen: Zustand = { ...z, schuld }
  if (besitzVon(offen, offen.amZug).length === 0) {
    // Nichts mehr zu versteigern: die Forderung beendet die Teilnahme sofort.
    return bankrott(offen)
  }
  return {
    ...notiere(offen, `kann ${betrag(wert)} (${grund}) nicht bar zahlen und muss versteigern.`),
    phase: 'schuld',
  }
}

/** Prueft nach einer Einnahme, ob eine offene Schuld jetzt beglichen werden kann. */
function pruefeSchuld(z: Zustand): Zustand {
  const schuld = z.schuld
  if (!schuld) return z
  const s = spieler(z, z.amZug)
  if (s.konto < schuld.betrag) {
    if (besitzVon(z, z.amZug).length > 0) return { ...z, phase: 'schuld' }
    return bankrott(z)
  }
  let out = ueberweise(z, z.amZug, schuld.an, schuld.betrag)
  out = notiere(out, `begleicht ${betrag(schuld.betrag)} (${schuld.grund}).`)
  out = { ...out, schuld: null }
  return fuehreOffenenBauAus(out)
}

function bankrott(z: Zustand): Zustand {
  const schuld = z.schuld
  const s = spieler(z, z.amZug)
  let out = z
  if (schuld && s.konto > 0) out = ueberweise(out, z.amZug, schuld.an, s.konto)
  out = mitSpieler(out, z.amZug, { konto: 0, ausgeschieden: true })
  out = notiere(out, 'ist bankrott und scheidet aus.')
  out = { ...out, schuld: null, offenerBau: null, phase: 'handeln' }
  return naechsterSpieler(out)
}

function fuehreOffenenBauAus(z: Zustand): Zustand {
  const bau = z.offenerBau
  if (!bau) return { ...z, phase: 'handeln' }
  const b = z.besitz[bau.hotel]
  if (!b || b.besitzer !== z.amZug) {
    // Das Grundstueck wurde zur Finanzierung selbst verkauft: die Genehmigung
    // verfaellt, gezahlt wird dafuer nichts.
    return {
      ...notiere(z, 'hat das Grundstück verkauft, die Baugenehmigung verfällt.'),
      offenerBau: null,
      phase: 'handeln',
    }
  }
  const out = mitBesitz(
    z,
    bau.hotel,
    mitFertigstellung(z, { ...b, gebaeude: b.gebaeude + bau.anzahl }),
  )
  return {
    ...notiere(out, `errichtet ${bau.anzahl === 1 ? 'ein Gebäude' : bau.anzahl + ' Gebäude'} am ${HOTELS[bau.hotel].name}.`),
    offenerBau: null,
    phase: 'handeln',
  }
}

// ---------------------------------------------------------------------------
// Auktionen
// ---------------------------------------------------------------------------

function bieterKreis(z: Zustand, verkaeufer: SpielerId): readonly SpielerId[] {
  return aktive(z)
    .map((s) => s.id)
    .filter((id) => id !== verkaeufer)
}

function starteAuktion(z: Zustand, hotel: HotelId): Zustand {
  const kreis = bieterKreis(z, z.amZug).filter(
    (id) => !z.auktion?.ausgestiegen.includes(id),
  )
  if (kreis.length === 0) return auktionAbschliessen(z, { ...leereAuktion(z, hotel) })
  const auktion: Auktion = {
    hotel,
    verkaeufer: z.amZug,
    hoechstgebot: 0,
    hoechstbieter: null,
    amZug: kreis[0]!,
    ausgestiegen: [],
  }
  return {
    ...notiere(z, `versteigert ${HOTELS[hotel].name}.`),
    auktion,
    phase: 'auktion',
  }
}

function leereAuktion(z: Zustand, hotel: HotelId): Auktion {
  return {
    hotel,
    verkaeufer: z.amZug,
    hoechstgebot: 0,
    hoechstbieter: null,
    amZug: z.amZug,
    ausgestiegen: [],
  }
}

function naechsterBieter(z: Zustand, a: Auktion): SpielerId | null {
  const kreis = bieterKreis(z, a.verkaeufer).filter((id) => !a.ausgestiegen.includes(id))
  if (kreis.length === 0) return null
  if (kreis.length === 1 && a.hoechstbieter === kreis[0]) return null
  const start = kreis.indexOf(a.amZug)
  for (let i = 1; i <= kreis.length; i++) {
    const kandidat = kreis[(start + i) % kreis.length]!
    if (kandidat !== a.hoechstbieter) return kandidat
  }
  return null
}

function auktionAbschliessen(z: Zustand, a: Auktion): Zustand {
  let out: Zustand = { ...z, auktion: null }
  const b = out.besitz[a.hotel]
  if (!b) return { ...out, phase: 'handeln' }

  if (a.hoechstbieter === null) {
    out = mitBesitz(out, a.hotel, null)
    out = { ...out, eingaenge: out.eingaenge.filter((e) => e.hotel !== a.hotel) }
    out = notiere(out, `${HOTELS[a.hotel].name} findet keinen Käufer und wird abgerissen.`)
  } else {
    out = ueberweise(out, a.hoechstbieter, a.verkaeufer, a.hoechstgebot)
    out = mitBesitz(out, a.hotel, { ...b, besitzer: a.hoechstbieter })
    out = notiere(
      out,
      `ersteigert ${HOTELS[a.hotel].name} für ${betrag(a.hoechstgebot)}.`,
      a.hoechstbieter,
    )
  }
  return pruefeSchuld({ ...out, phase: 'handeln' })
}

// ---------------------------------------------------------------------------
// Moegliche Aktionen
// ---------------------------------------------------------------------------

function naechsteStufeKosten(hotel: Hotel, gebaeude: number, anzahl: number): number {
  let s = 0
  for (let i = 0; i < anzahl; i++) s += hotel.gebaeude[gebaeude + i]?.kosten ?? 0
  return s
}

export function moeglicheAktionen(z: Zustand): readonly Aktion[] {
  switch (z.phase) {
    case 'wuerfeln':
      return [{ art: 'wuerfeln' }]
    case 'uebernachten':
      return [{ art: 'uebernachten' }]
    case 'schuld':
      return besitzVon(z, z.amZug).map((b) => ({ art: 'versteigern', hotel: b.hotel }) as const)
    case 'auktion':
      return auktionsAktionen(z)
    case 'ende':
      return []
    case 'handeln':
      return handelsAktionen(z)
  }
}

/** Mindest- und Hoechstgebot des Spielers, der gerade bieten darf. */
export function gebotsSpanne(z: Zustand): { min: number; max: number } | null {
  const a = z.auktion
  if (!a) return null
  const min = a.hoechstgebot + 50
  const max = spieler(z, a.amZug).konto
  return max >= min ? { min, max } : null
}

function auktionsAktionen(z: Zustand): readonly Aktion[] {
  const spanne = gebotsSpanne(z)
  const out: Aktion[] = [{ art: 'passen' }]
  if (spanne) out.push({ art: 'bieten', betrag: spanne.min })
  return out
}

/**
 * Ist diese Aktion jetzt erlaubt? Fuer alles ausser Geboten ist das die Frage, ob
 * sie in `moeglicheAktionen` vorkommt. Gebote sind ein Bereich, kein Einzelwert -
 * die Liste nennt nur das Mindestgebot.
 */
export function istErlaubt(z: Zustand, a: Aktion): boolean {
  if (a.art === 'bieten') {
    const spanne = gebotsSpanne(z)
    return !!spanne && a.betrag >= spanne.min && a.betrag <= spanne.max
  }
  const liste = moeglicheAktionen(z)
  return liste.some((x) => JSON.stringify(x) === JSON.stringify(a))
}

function handelsAktionen(z: Zustand): readonly Aktion[] {
  const out: Aktion[] = []
  const s = spieler(z, z.amZug)
  const feld = FELDER[s.feld]!

  // Grundstueck kaufen bzw. Zwangskauf
  if (!z.rechte.grundstueckGekauft && feld.art === 'kauf') {
    for (const hotel of angrenzendeGelaende(feld)) {
      const b = z.besitz[hotel]
      if (!b && s.konto >= HOTELS[hotel].grundstueck) {
        out.push({ art: 'grundstueck-kaufen', hotel })
      } else if (
        b &&
        b.besitzer !== z.amZug &&
        b.gebaeude === 0 &&
        s.konto >= HOTELS[hotel].zwangsverkauf
      ) {
        out.push({ art: 'zwangskauf', hotel })
      }
    }
  }

  const eigene = besitzVon(z, z.amZug)

  // Baugenehmigung beantragen
  if (z.rechte.darfGenehmigungBeantragen) {
    for (const b of eigene) {
      if (b.erworbenInRunde >= z.runde) continue
      const offen = HOTELS[b.hotel].gebaeude.length - b.gebaeude
      for (let n = 1; n <= offen; n++) out.push({ art: 'baugenehmigung', hotel: b.hotel, anzahl: n })
    }
  }

  // Kostenlose Bauphase
  if (z.rechte.gratisBauphasen > 0) {
    for (const b of eigene) {
      if (b.erworbenInRunde >= z.runde) continue
      if (b.gebaeude < HOTELS[b.hotel].gebaeude.length) {
        out.push({ art: 'gratis-bauen', hotel: b.hotel })
      } else if (parkErlaubt(z, b)) {
        out.push({ art: 'gratis-freizeitpark', hotel: b.hotel })
      }
    }
  }

  // Eingaenge
  const vorrat = z.regeln.eingaengeVorrat - z.eingaenge.length
  if (vorrat > 0) {
    for (const b of eigene) {
      if (b.gebaeude === 0) continue
      const frei = freieEingangsFelder(z, b.hotel)
      if (frei.length === 0) continue
      if (z.rechte.gratisEingaenge > 0) {
        for (const f of frei) out.push({ art: 'gratis-eingang', hotel: b.hotel, feld: f })
      }
      if (
        z.rechte.rathaus &&
        !z.rechte.rathausGenutzt.includes(b.hotel) &&
        s.konto >= HOTELS[b.hotel].eingang
      ) {
        for (const f of frei) out.push({ art: 'eingang-kaufen', hotel: b.hotel, feld: f })
      }
    }
  }

  // Freizeitpark
  for (const b of eigene) {
    if (parkErlaubt(z, b) && s.konto >= HOTELS[b.hotel].freizeitpark.kosten) {
      out.push({ art: 'freizeitpark-kaufen', hotel: b.hotel })
    }
  }

  out.push({ art: 'zug-beenden' })
  if (z.rechte.nochmal) out.push({ art: 'zug-abgeben' })
  return out
}

// ---------------------------------------------------------------------------
// Aktionen anwenden
// ---------------------------------------------------------------------------

export function anwenden(z: Zustand, a: Aktion): Zustand {
  switch (a.art) {
    case 'wuerfeln':
      return tuWuerfeln(z)
    case 'uebernachten':
      return tuUebernachten(z)
    case 'grundstueck-kaufen':
      return tuGrundstueckKaufen(z, a.hotel, false)
    case 'zwangskauf':
      return tuGrundstueckKaufen(z, a.hotel, true)
    case 'baugenehmigung':
      return tuBaugenehmigung(z, a.hotel, a.anzahl)
    case 'gratis-bauen':
      return tuGratisBauen(z, a.hotel)
    case 'gratis-freizeitpark':
      return tuFreizeitpark(z, a.hotel, true)
    case 'freizeitpark-kaufen':
      return tuFreizeitpark(z, a.hotel, false)
    case 'eingang-kaufen':
      return tuEingang(z, a.hotel, a.feld, false)
    case 'gratis-eingang':
      return tuEingang(z, a.hotel, a.feld, true)
    case 'versteigern':
      return starteAuktion(z, a.hotel)
    case 'bieten':
      return tuBieten(z, a.betrag)
    case 'passen':
      return tuPassen(z)
    case 'zug-beenden':
      return naechsterSpieler(z)
    case 'zug-abgeben':
      return naechsterSpieler(
        notiere(mitRechten(z, { nochmal: false }), 'verzichtet auf den Zusatzzug.'),
      )
  }
}

function tuWuerfeln(z: Zustand): Zustand {
  const [augen, r] = wuerfel(z.zufall)
  let out: Zustand = { ...z, zufall: r, anzeige: { ...LEERE_ANZEIGE, wurf: augen } }
  out = notiere(out, `würfelt ${augen}.`)
  out = mitRechten(out, { nochmal: augen === 6 })
  out = bewege(out, augen)
  return nachAnkunft(out)
}

function tuUebernachten(z: Zustand): Zustand {
  const feld = spieler(z, z.amZug).feld
  const eingang = eingangAuf(z, feld)
  if (!eingang) return { ...z, phase: 'handeln' }
  const [naechte, r] = wuerfel(z.zufall)
  const satz = nachtsatz(z, eingang.hotel)
  const rechnung = naechte * satz
  let out: Zustand = { ...z, zufall: r }
  out = notiere(
    out,
    `übernachtet ${naechte === 1 ? 'eine Nacht' : naechte + ' Nächte'} im ${HOTELS[eingang.hotel].name} (${sterne(out, eingang.hotel)} Sterne, ${betrag(satz)} je Nacht).`,
  )
  out = {
    ...out,
    offeneRechnung: { hotel: eingang.hotel, naechte, betrag: rechnung },
    anzeige: {
      ...out.anzeige,
      naechte,
      rechnung: {
        hotel: eingang.hotel,
        naechte,
        satz,
        betrag: rechnung,
        an: eingang.besitzer,
      },
    },
  }
  out = fordere(out, rechnung, eingang.besitzer, 'Übernachtung')
  return out.phase === 'schuld' ? out : { ...out, phase: 'handeln', offeneRechnung: null }
}

function tuGrundstueckKaufen(z: Zustand, hotel: HotelId, zwang: boolean): Zustand {
  const h = HOTELS[hotel]
  const alt = z.besitz[hotel]
  const preis = zwang ? h.zwangsverkauf : h.grundstueck
  let out = z
  if (zwang && alt) {
    out = ueberweise(out, z.amZug, alt.besitzer, preis)
    out = notiere(
      out,
      `kauft ${h.name} im Zwangsverkauf für ${betrag(preis)} von ${spieler(out, alt.besitzer).name}.`,
    )
    out = mitBesitz(out, hotel, { ...alt, besitzer: z.amZug, erworbenInRunde: z.runde })
  } else {
    out = ueberweise(out, z.amZug, 'bank', preis)
    out = notiere(out, `kauft das Grundstück ${h.name} für ${betrag(preis)}.`)
    out = mitBesitz(out, hotel, {
      hotel,
      besitzer: z.amZug,
      gebaeude: 0,
      freizeitpark: false,
      erworbenInRunde: z.runde,
      komplettSeitRunde: null,
    })
  }
  return mitRechten(out, { grundstueckGekauft: true })
}

function tuBaugenehmigung(z: Zustand, hotel: HotelId, anzahl: number): Zustand {
  const b = z.besitz[hotel]
  if (!b) return z
  const h = HOTELS[hotel]
  const [seite, r] = bauwuerfel(z.zufall)
  let out: Zustand = mitRechten(
    { ...z, zufall: r, anzeige: { ...z.anzeige, bauwurf: seite } },
    { darfGenehmigungBeantragen: false },
  )

  if (seite === 'rot') {
    return notiere(out, `beantragt eine Baugenehmigung für ${h.name} - abgelehnt.`)
  }

  const grundpreis = naechsteStufeKosten(h, b.gebaeude, anzahl)
  const preis = seite === 'h' ? 0 : seite === 'doppelt' ? grundpreis * 2 : grundpreis
  const hinweis =
    seite === 'h'
      ? 'genehmigt und kostenlos'
      : seite === 'doppelt'
        ? 'genehmigt, aber zum doppelten Preis'
        : 'genehmigt'
  out = notiere(out, `beantragt eine Baugenehmigung für ${h.name} - ${hinweis}.`)

  out = { ...out, offenerBau: { hotel, anzahl } }
  if (preis === 0) return fuehreOffenenBauAus(out)
  out = fordere(out, preis, 'bank', `Bau am ${h.name}`)
  return out.phase === 'schuld' ? out : fuehreOffenenBauAus(out)
}

function tuGratisBauen(z: Zustand, hotel: HotelId): Zustand {
  const b = z.besitz[hotel]
  if (!b || b.gebaeude >= HOTELS[hotel].gebaeude.length) return z
  let out = mitBesitz(z, hotel, mitFertigstellung(z, { ...b, gebaeude: b.gebaeude + 1 }))
  out = mitRechten(out, { gratisBauphasen: out.rechte.gratisBauphasen - 1 })
  return notiere(out, `baut kostenlos am ${HOTELS[hotel].name}.`)
}

function tuFreizeitpark(z: Zustand, hotel: HotelId, gratis: boolean): Zustand {
  const b = z.besitz[hotel]
  if (!b || !parkErlaubt(z, b)) return z
  let out = mitBesitz(z, hotel, { ...b, freizeitpark: true })
  if (gratis) {
    out = mitRechten(out, { gratisBauphasen: out.rechte.gratisBauphasen - 1 })
    out = notiere(out, `legt kostenlos den Freizeitpark am ${HOTELS[hotel].name} an.`)
  } else {
    out = ueberweise(out, z.amZug, 'bank', HOTELS[hotel].freizeitpark.kosten)
    out = notiere(
      out,
      `legt den Freizeitpark am ${HOTELS[hotel].name} für ${betrag(HOTELS[hotel].freizeitpark.kosten)} an.`,
    )
  }
  return out
}

function tuEingang(z: Zustand, hotel: HotelId, feld: number, gratis: boolean): Zustand {
  if (z.eingaenge.some((e) => e.feld === feld)) return z
  let out: Zustand = { ...z, eingaenge: [...z.eingaenge, { feld, hotel }] }
  if (gratis) {
    out = mitRechten(out, { gratisEingaenge: out.rechte.gratisEingaenge - 1 })
    out = notiere(out, `stellt kostenlos einen Eingang zum ${HOTELS[hotel].name} auf Feld ${feld}.`)
  } else {
    out = ueberweise(out, z.amZug, 'bank', HOTELS[hotel].eingang)
    out = mitRechten(out, { rathausGenutzt: [...out.rechte.rathausGenutzt, hotel] })
    out = notiere(
      out,
      `kauft einen Eingang zum ${HOTELS[hotel].name} auf Feld ${feld} für ${betrag(HOTELS[hotel].eingang)}.`,
    )
  }
  return out
}

function tuBieten(z: Zustand, wert: number): Zustand {
  const a = z.auktion
  if (!a) return z
  const spanne = gebotsSpanne(z)
  if (!spanne || wert < spanne.min || wert > spanne.max) return z
  const neu: Auktion = { ...a, hoechstgebot: wert, hoechstbieter: a.amZug }
  const naechster = naechsterBieter(z, neu)
  const notiert = notiere(z, `bietet ${betrag(wert)}.`, a.amZug)
  if (naechster === null) return auktionAbschliessen(notiert, neu)
  return { ...notiert, auktion: { ...neu, amZug: naechster } }
}

function tuPassen(z: Zustand): Zustand {
  const a = z.auktion
  if (!a) return z
  const neu: Auktion = { ...a, ausgestiegen: [...a.ausgestiegen, a.amZug] }
  const naechster = naechsterBieter(z, neu)
  const notiert = notiere(z, 'passt.', a.amZug)
  if (naechster === null) return auktionAbschliessen(notiert, neu)
  return { ...notiert, auktion: { ...neu, amZug: naechster } }
}
